// src/pages/admin/Finanzas.js
import React, { useEffect, useState } from 'react';
import api from '../../utils/api';
import toast from 'react-hot-toast';

const fmtCOP = v => new Intl.NumberFormat('es-CO',{style:'currency',currency:'COP',minimumFractionDigits:0}).format(v||0);
const CATEGORIAS = { ingreso:['Ventas','Otro ingreso'], costo:['Materia prima','Envases','Mano de obra','Producción'], gasto:['Marketing','Transporte','Servicios','Nómina','Operativo','Otro'] };

export default function Finanzas() {
  const [data, setData] = useState({ ingresos:0, costos:0, gastos:0, utilidad:0, movimientos:[] });
  const [form, setForm] = useState({ tipo:'gasto', categoria:'Marketing', concepto:'', valor:'', fecha: new Date().toISOString().split('T')[0], notas:'' });
  const [loading, setLoading] = useState(false);

  // Calculadora de costo
  const [calc, setCalc] = useState({ mp:0, envase:0, mano:0, indirecto:20 });
  const costoBase = parseFloat(calc.mp||0) + parseFloat(calc.envase||0) + parseFloat(calc.mano||0);
  const costoTotal = costoBase * (1 + parseFloat(calc.indirecto||0)/100);
  const precioSug = costoTotal * 2.5;
  const margen = costoTotal > 0 ? Math.round((precioSug - costoTotal) / precioSug * 100) : 0;

  const load = () => api.get('/finanzas/resumen').then(r => setData(r.data)).catch(()=>{});
  useEffect(() => { load(); }, []);

  const guardar = async () => {
    if (!form.concepto || !form.valor) return toast.error('Concepto y valor requeridos');
    setLoading(true);
    try {
      await api.post('/finanzas/movimiento', form);
      toast.success('Movimiento registrado ✅');
      setForm(f => ({ ...f, concepto:'', valor:'', notas:'' }));
      load();
    } catch { toast.error('Error al guardar'); }
    finally { setLoading(false); }
  };

  const tipoColor = { ingreso:'var(--green)', costo:'var(--red)', gasto:'var(--orange)' };
  const tipoBadge = { ingreso:'badge-green', costo:'badge-red', gasto:'badge-yellow' };

  return (
    <div>
      <div style={{ background:'#fff', borderBottom:'1px solid var(--border)', padding:'14px 28px', position:'sticky', top:0, zIndex:10 }}>
        <h1 style={{ fontSize:18, fontWeight:900, color:'var(--forest)' }}>Costos, Gastos e Ingresos</h1>
      </div>
      <div style={{ padding:'24px 28px' }}>
        {/* Métricas */}
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(140px, 1fr))', gap:16, marginBottom:24 }}>
          {[
            { label:'Ingresos mes', value:data.ingresos, color:'var(--green)' },
            { label:'Costos producción', value:data.costos, color:'var(--red)' },
            { label:'Gastos operativos', value:data.gastos, color:'var(--orange)' },
            { label:'Utilidad neta', value:data.utilidad, color:'var(--green)' },
          ].map(m => (
            <div className="metric-card" key={m.label}>
              <div className="metric-label">{m.label}</div>
              <div className="metric-value" style={{ color:m.color, fontSize:20 }}>{fmtCOP(m.value)}</div>
            </div>
          ))}
        </div>

        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(300px, 1fr))', gap:20 }}>
          {/* Registrar movimiento */}
          <div className="card">
            <div className="card-header"><span className="card-title">Registrar movimiento</span></div>
            <div className="card-body">
              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(200px, 1fr))', gap:12 }}>
                <div className="form-group">
                  <label className="form-label">Tipo</label>
                  <select className="form-input" value={form.tipo} onChange={e => setForm(f => ({ ...f, tipo:e.target.value, categoria: CATEGORIAS[e.target.value][0] }))}>
                    <option value="ingreso">💰 Ingreso</option>
                    <option value="costo">🏭 Costo producción</option>
                    <option value="gasto">📤 Gasto operativo</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Categoría</label>
                  <select className="form-input" value={form.categoria} onChange={e => setForm(f => ({ ...f, categoria:e.target.value }))}>
                    {CATEGORIAS[form.tipo].map(c => <option key={c}>{c}</option>)}
                  </select>
                </div>
                <div className="form-group" style={{ gridColumn:'1/-1' }}>
                  <label className="form-label">Concepto</label>
                  <input className="form-input" value={form.concepto} onChange={e => setForm(f => ({ ...f, concepto:e.target.value }))} placeholder="Ej: Aceite de coco 2kg" />
                </div>
                <div className="form-group">
                  <label className="form-label">Valor (COP)</label>
                  <input className="form-input" type="number" value={form.valor} onChange={e => setForm(f => ({ ...f, valor:e.target.value }))} placeholder="0" />
                </div>
                <div className="form-group">
                  <label className="form-label">Fecha</label>
                  <input className="form-input" type="date" value={form.fecha} onChange={e => setForm(f => ({ ...f, fecha:e.target.value }))} />
                </div>
                <div className="form-group" style={{ gridColumn:'1/-1' }}>
                  <label className="form-label">Notas (opcional)</label>
                  <input className="form-input" value={form.notas} onChange={e => setForm(f => ({ ...f, notas:e.target.value }))} placeholder="Referencia, proveedor, etc." />
                </div>
              </div>
              <button className="btn btn-primary" style={{ width:'100%' }} onClick={guardar} disabled={loading}>
                {loading ? 'Guardando...' : 'Registrar movimiento'}
              </button>
            </div>
          </div>

          {/* Calculadora de costo de producto */}
          <div className="card">
            <div className="card-header"><span className="card-title">🧮 Calculadora de costo</span></div>
            <div className="card-body">
              {[
                { key:'mp', label:'Materias primas', ph:'0' },
                { key:'envase', label:'Envase / empaque', ph:'0' },
                { key:'mano', label:'Mano de obra', ph:'0' },
                { key:'indirecto', label:'Gastos indirectos %', ph:'20' },
              ].map(f => (
                <div className="form-group" key={f.key}>
                  <label className="form-label">{f.label}</label>
                  <input className="form-input" type="number" value={calc[f.key]} onChange={e => setCalc(c => ({ ...c, [f.key]:e.target.value }))} placeholder={f.ph} />
                </div>
              ))}
              <div style={{ background:'var(--green-light)', borderRadius:10, padding:14, marginTop:8 }}>
                <div style={{ display:'flex', justifyContent:'space-between', fontSize:13, marginBottom:6 }}>
                  <span>Costo total:</span><span style={{ fontWeight:800 }}>{fmtCOP(costoTotal)}</span>
                </div>
                <div style={{ display:'flex', justifyContent:'space-between', fontSize:13, marginBottom:6 }}>
                  <span>Precio sugerido (2.5×):</span><span style={{ fontWeight:800, color:'var(--green)' }}>{fmtCOP(precioSug)}</span>
                </div>
                <div style={{ display:'flex', justifyContent:'space-between', fontSize:14, fontWeight:800 }}>
                  <span>Margen estimado:</span><span style={{ color:'var(--green)' }}>{margen}%</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Historial */}
        <div className="card" style={{ marginTop:20 }}>
          <div className="card-header"><span className="card-title">Historial de movimientos (mes actual)</span></div>
          <table className="table">
            <thead><tr><th>Fecha</th><th>Tipo</th><th>Categoría</th><th>Concepto</th><th>Valor</th></tr></thead>
            <tbody>
              {data.movimientos.map(m => (
                <tr key={m.id}>
                  <td style={{ fontSize:12 }}>{m.fecha}</td>
                  <td><span className={`badge ${tipoBadge[m.tipo]}`}>{m.tipo}</span></td>
                  <td style={{ fontSize:12 }}>{m.categoria}</td>
                  <td>{m.concepto}</td>
                  <td style={{ fontWeight:800, color: tipoColor[m.tipo] }}>{m.tipo==='ingreso'?'+':'-'} {fmtCOP(m.valor)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
